import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle, ArrowLeft, CheckCircle2, MapPin, CalendarRange, User as UserIcon,
  HardHat, Building2, Pencil, Trash2, X, BadgeEuro, ListChecks,
} from 'lucide-react';
import { ContactPicker } from '@/components/ContactPicker';
import { GewerkAccordion } from '@/components/GewerkAccordion';
import type { BauvorhabenContact, BauvorhabenContactRole, BauvorhabenStatus } from '@/types';
import type { BauvorhabenDetailContext } from './BauvorhabenDetailContext';

// Reine DATE-Spalten (YYYY-MM-DD) direkt als dd.MM.yyyy rendern —
// kein new Date(), damit keine Zeitzone ins Spiel kommt.
function formatDate(dateStr?: string | null): string {
  if (!dateStr) return "";
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return dateStr;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

const statusLabels: Record<BauvorhabenStatus, string> = {
  geplant: "Geplant",
  aktiv: "Aktiv",
  abgeschlossen: "Abgeschlossen",
  abgebrochen: "Abgebrochen",
};

const statusBadgeClasses: Record<BauvorhabenStatus, string> = {
  geplant: "bg-gray-100 text-gray-800",
  aktiv: "bg-green-100 text-green-800",
  abgeschlossen: "bg-blue-100 text-blue-800",
  abgebrochen: "bg-red-100 text-red-800",
};

interface SectionConfig {
  role: BauvorhabenContactRole;
  title: string;
  emptyText: string;
}

const sections: SectionConfig[] = [
  { role: 'bauherr', title: 'Bauherren', emptyText: 'Noch keine Bauherren zugeordnet.' },
  { role: 'sonstiges', title: 'Sonstige Kontakte', emptyText: 'Noch keine weiteren Kontakte zugeordnet.' },
];

function displayName(contact: BauvorhabenContact): string {
  return (
    contact.display_name ||
    [contact.first_name, contact.last_name].filter(Boolean).join(' ') ||
    contact.company_name ||
    contact.email_address ||
    'Unbenannter Kontakt'
  );
}

/**
 * Leseansicht des Bauvorhaben-Details: Stammdaten + Zuordnungen
 * (Bauherren/Sonstiges, Gewerk-Akkordeon). State/Handler kommen als Prop
 * aus der Detail-Layoutroute (kein useOutletContext — die Leseansicht
 * hängt NICHT im Outlet); die Bearbeiten-Spalte öffnet daneben via Route.
 */
export function BauvorhabenReadView({ context }: { context: BauvorhabenDetailContext }) {
  const navigate = useNavigate();
  const {
    bauvorhaben, contacts, accordion, formData, isApprover,
    associationError, associationInfo,
    remove, addContact, removeContact,
    changeGewerkStatus, addGewerkContact, removeGewerkContact,
    openContactFromLink,
  } = context;

  const addressParts = [formData.street, formData.postal_code, formData.city].filter(Boolean).join(' ');

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <Link to="/bauvorhaben" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft className="h-4 w-4" />
          Bauvorhaben
        </Link>
        {isApprover && (
          <div className="flex gap-2">
            <button onClick={() => navigate('edit')} className="btn btn-secondary inline-flex items-center gap-2">
              <Pencil className="h-4 w-4" />
              Bearbeiten
            </button>
            <button onClick={remove} className="btn btn-danger inline-flex items-center gap-2">
              <Trash2 className="h-4 w-4" />
              Löschen
            </button>
          </div>
        )}
      </div>

      {/* Stammdaten */}
      <div className="card p-6 mb-6">
        <>
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{bauvorhaben?.name}</h1>
              {bauvorhaben?.project_number && (
                <p className="text-sm text-gray-500">Projekt-Nr. {bauvorhaben.project_number}</p>
              )}
            </div>
            {bauvorhaben && (
              <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClasses[bauvorhaben.status]}`}>
                {statusLabels[bauvorhaben.status]}
              </span>
            )}
          </div>
          <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <div className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              <div>
                <dt className="text-gray-500">Adresse</dt>
                <dd className="text-gray-900">{addressParts || '—'}</dd>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <UserIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              <div>
                <dt className="text-gray-500">Bauleiter</dt>
                <dd className="text-gray-900">
                  {bauvorhaben?.bauleiter_name
                    ? bauvorhaben.bauleiter_contact_id
                      ? (
                        <Link
                          to={`/contacts/${bauvorhaben.bauleiter_contact_id}`}
                          onClick={(e) =>
                            openContactFromLink(e, bauvorhaben.bauleiter_contact_id!, bauvorhaben.bauleiter_name ?? undefined)
                          }
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {bauvorhaben.bauleiter_name}
                        </Link>
                      )
                      : bauvorhaben.bauleiter_name
                    : '—'}
                </dd>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CalendarRange className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              <div>
                <dt className="text-gray-500">Zeitraum</dt>
                <dd className="text-gray-900">
                  {(bauvorhaben?.start_date || bauvorhaben?.end_date)
                    ? [formatDate(bauvorhaben?.start_date), formatDate(bauvorhaben?.end_date)]
                        .filter(Boolean)
                        .join(' – ') || '—'
                    : '—'}
                </dd>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <BadgeEuro className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              <div>
                <dt className="text-gray-500">Vertriebler</dt>
                <dd className="text-gray-900">
                  {bauvorhaben?.vertriebler_name
                    ? bauvorhaben.vertriebler_contact_id
                      ? (
                        <Link
                          to={`/contacts/${bauvorhaben.vertriebler_contact_id}`}
                          onClick={(e) =>
                            openContactFromLink(e, bauvorhaben.vertriebler_contact_id!, bauvorhaben.vertriebler_name ?? undefined)
                          }
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {bauvorhaben.vertriebler_name}
                        </Link>
                      )
                      : bauvorhaben.vertriebler_name
                    : '—'}
                </dd>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <HardHat className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              <div>
                <dt className="text-gray-500">Kontakte</dt>
                <dd className="text-gray-900">
                  {contacts.filter((c) => c.role === 'bauherr').length} Bauherren
                </dd>
              </div>
            </div>
            {bauvorhaben?.notes && (
              <div className="sm:col-span-2">
                <dt className="text-gray-500">Notizen</dt>
                <dd className="whitespace-pre-wrap text-gray-900">{bauvorhaben.notes}</dd>
              </div>
            )}
          </dl>
        </>
      </div>

      {/* Zuordnungen */}
      <div className="space-y-6">
        {associationError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {associationError}
          </div>
        )}
        {associationInfo && (
          <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            {associationInfo}
          </div>
        )}
        {sections.map((section) => {
          const sectionContacts = contacts.filter((c) => c.role === section.role);
          return (
            <div key={section.role} className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{section.title}</h2>
              {sectionContacts.length === 0 ? (
                <p className="text-sm text-gray-500 mb-4">{section.emptyText}</p>
              ) : (
                <ul className="mb-4 divide-y divide-gray-100">
                  {sectionContacts.map((contact) => (
                    <li key={contact.link_id} className="flex items-center justify-between gap-3 py-2">
                      <Link
                        to={`/contacts/${contact.contact_id}`}
                        onClick={(e) => openContactFromLink(e, contact.contact_id, displayName(contact))}
                        className="min-w-0 flex-1 hover:text-primary-700"
                      >
                        <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                          <Building2 className="h-4 w-4 shrink-0 text-gray-400" />
                          {displayName(contact)}
                        </span>
                        {(contact.email_address || contact.business_phone) && (
                          <span className="ml-6 block truncate text-xs text-gray-500">
                            {[contact.email_address, contact.business_phone].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </Link>
                      {isApprover && (
                        <button
                          onClick={() => removeContact(contact.link_id)}
                          className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                          title="Zuordnung entfernen"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {isApprover && (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <ContactPicker
                    onSelect={(contact) => addContact(section.role, contact)}
                    placeholder={section.role === 'bauherr' ? 'Bauherr suchen…' : 'Kontakt suchen…'}
                  />
                </div>
              )}
            </div>
          );
        })}

        {/* Gewerke — Bauablauf-Checkliste (gesamter aktiver Katalog) */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-gray-400" />
            Gewerke — Bauablauf-Checkliste
          </h2>
          {accordion ? (
            <GewerkAccordion
              payload={accordion}
              isApprover={isApprover}
              onStatusChange={changeGewerkStatus}
              onAddContact={addGewerkContact}
              onRemoveContact={removeGewerkContact}
            />
          ) : (
            <p className="text-sm text-gray-500">Gewerke konnten nicht geladen werden.</p>
          )}
        </div>
      </div>
    </div>
  );
}